import React, { useEffect, useRef, useState } from "react";
import { Shield, Plus, Trash2, Radio, CalendarClock, Loader2, Pencil, Megaphone, Upload, Check, X, HandHeart, MapPin, CalendarDays, Star, Users as UsersIcon, UserPlus, ScrollText, Mail, Download, Activity, KeyRound, Send, Eye, Link as LinkIcon, Image as ImageIcon, GripVertical, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/pages/MySpots";
import Waveform from "@/components/Waveform";
import ListenerMap from "@/components/ListenerMap";
import AdminAnalytics from "@/components/AdminAnalytics";
import EventEditModal from "@/components/EventEditModal";
import AiAdGenerator from "@/components/AiAdGenerator";
import DevotionManager from "@/components/DevotionManager";
import SocialMedia from "@/components/SocialMedia";
import JingleStudio from "@/components/JingleStudio";
import SmsManager from "@/components/SmsManager";
import { Sparkles, TrendingUp, BarChart3, Clock, GitCompare } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { API } from "@/lib/api";
import { formatUSPhone } from "@/lib/phone";
import { fmtDate, fmtDateTime } from "@/lib/date";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";

const emptyStation = { name: "", stream_url: "", genre: "Gospel", frequency: "", description: "", image_url: "", ad_interval: 180 };
const emptyProgram = { title: "", host: "", station_id: "", day: "Monday", start_time: "", end_time: "", description: "", takes_calls: false };
const emptyUser = { name: "", email: "", password: "", role: "user", title: "", phone: "", birth_date: "", city: "", state: "", country: "United States" };
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ROLE_STYLES = { admin: "bg-[#5B7CFA]/15 text-[#8CA6FF]", host: "bg-[#E6B34A]/15 text-[#E6B34A]", user: "bg-white/10 text-zinc-300" };

function SpotAudioPreview({ spot }) {
  if (!spot.audio_url) return null;
  const token = localStorage.getItem("token");
  return <Waveform url={`${API}/spots/${spot.id}/audio?auth=${token}`} testid={`admin-wave-${spot.id}`} />;
}

const AIRING_STYLES = {
  airing: "bg-emerald-400/15 text-emerald-400",
  waiting: "bg-[#E6B34A]/15 text-[#E6B34A]",
  scheduled: "bg-[#5B7CFA]/15 text-[#8CA6FF]",
  capped: "bg-orange-400/15 text-orange-300",
  done: "bg-white/10 text-zinc-400",
  idle: "bg-white/10 text-zinc-500",
};

function AiringBadge({ airing }) {
  const cls = AIRING_STYLES[airing.state] || AIRING_STYLES.idle;
  return (
    <div className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`} data-testid="airing-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${airing.state === "airing" ? "bg-emerald-400 animate-pulse" : "bg-current opacity-70"}`} />
      {airing.label}
    </div>
  );
}

export default function Admin() {
  const [stations, setStations] = useState([]);
  const [health, setHealth] = useState({});
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [spots, setSpots] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [approvedEvents, setApprovedEvents] = useState([]);
  const [settings, setSettings] = useState({ hero_subtitle: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoVer, setLogoVer] = useState(0);
  const [hosts, setHosts] = useState([]);
  const [uploadingHost, setUploadingHost] = useState(null);
  const hostPhotoRefs = useRef({});
  const logoRef = useRef(null);
  const brand = useBrand();
  const [loading, setLoading] = useState(true);
  const [smsUnread, setSmsUnread] = useState(0);
  useEffect(() => {
    const f = () => api.get("/sms/unread-count").then((r) => setSmsUnread(r.data?.count || 0)).catch(() => {});
    f(); const t = setInterval(f, 20000); return () => clearInterval(t);
  }, []);
  const [stationForm, setStationForm] = useState(null);
  const [programForm, setProgramForm] = useState(null);
  const [houseAd, setHouseAd] = useState(null);
  const [editSpot, setEditSpot] = useState(null);
  const editLogoRef = useRef(null);
  const [savingSpot, setSavingSpot] = useState(false);
  const [aiAdOpen, setAiAdOpen] = useState(false);
  const [cloneDraft, setCloneDraft] = useState(null);
  const [perf, setPerf] = useState([]);
  const [perfRange, setPerfRange] = useState("all");
  const [chartOpen, setChartOpen] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [reportCfg, setReportCfg] = useState(null);
  const [rotation, setRotation] = useState({ enabled: false, cadence: "weekly", next_at: null });
  const toggleCompare = (id) => setCompareIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2));
  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const loadPerf = (r) => api.get(`/spots/performance?range=${r}`).then((res) => setPerf(res.data.spots || [])).catch(() => setPerf([]));
  const saveReportCfg = (patch) => {
    const next = { ...reportCfg, ...patch };
    setReportCfg(next);
    api.post("/spots/sponsor-report/settings", next).catch(() => toast.error("Could not save schedule"));
  };
  const exportPerfCsv = async () => {
    try {
      const res = await api.get(`/spots/performance/export?range=${perfRange}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ad-performance-${perfRange}.csv`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not export CSV"); }
  };
  const downloadSpotReport = async (id, name) => {
    try {
      const days = perfRange === "month" ? 30 : 7;
      const res = await api.get(`/spots/${id}/report.pdf?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report-${(name || "ad").replace(/[^a-z0-9]+/gi, "-")}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download report"); }
  };
  const sendSponsorReports = async () => {
    try {
      const pv = await api.get("/spots/sponsor-report/preview");
      if (!pv.data.sponsors) { toast.info("No sponsor plays in the last 7 days yet — nothing to send."); return; }
      if (!window.confirm(`Email a weekly play report to ${pv.data.sponsors} sponsor(s) (${pv.data.total_plays} plays total)?`)) return;
      const res = await api.post("/spots/sponsor-report");
      toast.success(`Sent ${res.data.sent} sponsor report${res.data.sent !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reports"); }
  };
  const duplicateSpot = async (id) => {
    try {
      const r = await api.post(`/ai-ads/drafts/from-spot/${id}`);
      setCloneDraft(r.data);
      setAiAdOpen(true);
      toast.success("Cloned to a new draft");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not duplicate"); }
  };
  const houseFileRef = useRef(null);
  const houseLogoRef = useRef(null);
  const { user: me, impersonate } = useAuth();
  const [presets, setPresets] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [legal, setLegal] = useState({ terms: "", privacy: "" });
  const [savingLegal, setSavingLegal] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [editEvent, setEditEvent] = useState(null);

  const load = () => {
    Promise.all([api.get("/stations"), api.get("/programs"), api.get("/spots"), api.get("/prayers"), api.get("/events/pending"), api.get("/events/admin"), api.get("/users")]).then(([s, p, sp, pr, ev, aev, us]) => {
      setStations(s.data); setPrograms(p.data); setSpots(sp.data); setPrayers(pr.data); setPendingEvents(ev.data); setApprovedEvents(aev.data); setUsers(us.data); setLoading(false);
    }).catch(() => setLoading(false));
  };
  const checkHealth = () => {
    setCheckingHealth(true);
    api.get("/stations/health").then((r) => setHealth(r.data || {})).catch(() => {}).finally(() => setCheckingHealth(false));
  };
  useEffect(() => { load(); checkHealth(); api.get("/admin/station-alerts").then((r) => setAlerts(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings").then((r) => { setSettings(r.data); setHosts(r.data.featured_hosts || []); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/legal").then((r) => setLegal(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/newsletter").then((r) => setSubscribers(r.data)).catch(() => {}); }, []);

  const [blast, setBlast] = useState({ subject: "", sendLater: false, sendAt: "" });
  const [segment, setSegment] = useState({ type: "all", value: "" });
  const [segCount, setSegCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [sendingBlast, setSendingBlast] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const blastEditorRef = React.useRef(null);

  const loadScheduled = () => api.get("/newsletter/scheduled").then((r) => setScheduled(r.data)).catch(() => {});
  const loadHistory = () => api.get("/newsletter/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadScheduled(); loadHistory(); }, []);
  useEffect(() => { loadPerf(perfRange); }, [perfRange]);
  useEffect(() => { api.get("/spots/sponsor-report/settings").then((r) => setReportCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/stations/rotation").then((r) => setRotation(r.data)).catch(() => {}); }, []);
  const saveRotation = async (patch) => {
    const next = { ...rotation, ...patch };
    setRotation(next);
    try {
      const { data } = await api.post("/stations/rotation", { enabled: next.enabled, cadence: next.cadence });
      setRotation(data);
      if (patch.enabled === true) { toast.success("Featured rotation on"); load(); }
      else if (patch.enabled === false) toast.success("Featured rotation off");
    } catch { toast.error("Could not save rotation"); }
  };
  useEffect(() => {
    if (segment.type === "city" && !segment.value.trim()) { setSegCount(null); return; }
    api.get("/newsletter/segment-count", { params: { type: segment.type, value: segment.value } }).then((r) => setSegCount(r.data.count)).catch(() => setSegCount(null));
  }, [segment.type, segment.value]);

  const exec = (cmd) => { document.execCommand(cmd, false, null); blastEditorRef.current && blastEditorRef.current.focus(); };
  const execLink = () => { const url = window.prompt("Link URL (https://…)"); if (url) document.execCommand("createLink", false, url); };
  const execImage = () => { const url = window.prompt("Image URL (https://…)"); if (url) document.execCommand("insertImage", false, url); };
  const getBlastHtml = () => (blastEditorRef.current ? blastEditorRef.current.innerHTML : "");

  const doSend = async ({ test }) => {
    const html = getBlastHtml().trim();
    if (!blast.subject.trim() || !html || html === "<br>") { toast.error("Subject and message are required"); return; }
    const payload = { subject: blast.subject, html };
    if (test) payload.test = true;
    else {
      payload.segment = { type: segment.type, value: segment.value };
      if (blast.sendLater) {
        if (!blast.sendAt) { toast.error("Pick a date and time to schedule"); return; }
        payload.send_at = new Date(blast.sendAt).toISOString();
      }
    }
    setSendingBlast(true);
    try {
      const res = await api.post("/newsletter/blast", payload);
      if (res.data.test) toast.success(`Test sent to ${res.data.sent_to}`);
      else if (res.data.scheduled) { toast.success(`Scheduled for ${fmtDateTime(res.data.send_at)}`); loadScheduled(); setBlast({ subject: "", sendLater: false, sendAt: "" }); if (blastEditorRef.current) blastEditorRef.current.innerHTML = ""; }
      else { toast.success(`Blast sent to ${res.data.recipients} recipient${res.data.recipients === 1 ? "" : "s"}`); loadHistory(); setBlast({ subject: "", sendLater: false, sendAt: "" }); if (blastEditorRef.current) blastEditorRef.current.innerHTML = ""; }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } finally { setSendingBlast(false); }
  };
  const cancelScheduled = async (id) => { await api.delete(`/newsletter/scheduled/${id}`); toast.success("Scheduled blast canceled"); loadScheduled(); };
  const [blastDetail, setBlastDetail] = useState(null);
  const openBlastDetail = (id) => api.get(`/newsletter/blast/${id}`).then((r) => setBlastDetail(r.data)).catch(() => toast.error("Could not load blast"));
  const resendUnopened = async () => {
    try { const r = await api.post(`/newsletter/blast/${blastDetail.id}/resend-unopened`); toast.success(`Reminder sent to ${r.data.recipients}`); setBlastDetail(null); loadHistory(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };

  const exportSubscribersCsv = () => {
    const rows = [["email", "subscribed_at"], ...subscribers.map((s) => [s.email || "", s.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${subscribers.length} subscriber${subscribers.length === 1 ? "" : "s"}`);
  };

  const saveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) { toast.error("Name and email are required"); return; }
    if (!userForm.id && userForm.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingUser(true);
    try {
      if (userForm.id) {
        const body = { name: userForm.name, email: userForm.email, role: userForm.role, title: userForm.title, phone: userForm.phone, birth_date: userForm.birth_date, city: userForm.city, state: userForm.state, country: userForm.country };
        if (userForm.password && userForm.password.trim()) body.password = userForm.password;
        await api.put(`/admin/users/${userForm.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", userForm);
        toast.success("User created");
      }
      setUserForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save user"); } finally { setSavingUser(false); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This also removes their events, requests and prayers.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const resetUserPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name || u.email}\n(at least 6 characters):`);
    if (pw === null) return;
    if (pw.trim().length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.put(`/admin/users/${u.id}`, { password: pw });
      toast.success(`Password reset for ${u.name || u.email}. They'll need to sign in again.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset password"); }
  };

  const doImpersonate = async (u) => {
    if (!window.confirm(`View the app as ${u.name || u.email}? You can return to your admin account anytime from the banner at the top.`)) return;
    try {
      await impersonate(u.id);
      toast.success(`Now viewing as ${u.name || u.email}`);
      window.location.assign("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not impersonate user"); }
  };

  const delPrayer = async (id) => {
    if (!window.confirm("Delete this prayer request?")) return;
    try { await api.delete(`/prayers/${id}`); toast.success("Prayer deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const delEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Event deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try { await api.put("/legal", legal); toast.success("Legal pages updated"); }
    catch (e) { toast.error("Could not save"); } finally { setSavingLegal(false); }
  };

  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const addHost = () => setHosts([...hosts, { name: "", role: "", photo: "", days: [0, 1, 2, 3, 4, 5, 6] }]);
  const updateHost = (i, patch) => setHosts(hosts.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeHost = (i) => setHosts(hosts.filter((_, idx) => idx !== i));
  const uploadHostPhoto = async (i, file) => {
    if (!file) return;
    setUploadingHost(i);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateHost(i, { photo: data.url });
      toast.success("Photo uploaded — remember to Save host lineup");
    } catch (e) { toast.error("Could not upload photo"); } finally { setUploadingHost(null); }
  };
  const toggleDay = (i, d) => {
    const h = hosts[i];
    const days = h.days.includes(d) ? h.days.filter((x) => x !== d) : [...h.days, d].sort();
    updateHost(i, { days });
  };
  const saveHosts = async () => {
    try {
      const { data } = await api.put("/settings/hosts", { featured_hosts: hosts.filter((h) => h.name.trim()) });
      setHosts(data.featured_hosts);
      brand.refresh();
      toast.success("Host lineup saved");
    } catch (e) { toast.error("Could not save hosts"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      brand.refresh();
      toast.success("Homepage text updated");
    } catch (e) { toast.error("Could not save settings"); } finally { setSavingSettings(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(data);
      brand.refresh();
      setLogoVer((v) => v + 1);
      toast.success("Logo updated");
    } catch (e) { toast.error("Could not upload logo"); } finally { setUploadingLogo(false); }
  };

  const resetLogo = async () => {
    try {
      const { data } = await api.delete("/settings/logo");
      setSettings(data);
      brand.refresh();
      setLogoVer((v) => v + 1);
      toast.success("Logo reset to default");
    } catch (e) { toast.error("Could not reset logo"); }
  };

  const reviewEvent = async (id, action) => {
    try {
      await api.post(`/events/${id}/review`, { action });
      toast.success(action === "approve" ? "Event approved 🎉" : "Event rejected");
      setPendingEvents((prev) => prev.filter((e) => e.id !== id));
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  const toggleFeature = async (ev) => {
    try {
      const { data } = await api.post(`/events/${ev.id}/feature`, { featured: !ev.featured });
      setApprovedEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, featured: data.featured } : e)));
      toast.success(data.featured ? "Pinned to home 🏠" : "Unpinned");
    } catch (e) { toast.error("Could not update"); }
  };

  const markPrayed = async (id) => {
    try {
      await api.put(`/prayers/${id}`, { status: "prayed" });
      toast.success("Marked as prayed 🙏");
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  const reviewSpot = async (id, action, reason) => {
    await api.put(`/spots/${id}/review`, { action, reason });
    toast.success(action === "approve" ? "Spot approved" : "Spot rejected");
    load();
  };
  const scheduleSpot = async (id, program_id) => {
    try {
      await api.put(`/spots/${id}/schedule`, { program_id: program_id || null });
      toast.success(program_id ? "Scheduled into program" : "Removed from schedule");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };
  const delSpot = async (id) => { await api.delete(`/spots/${id}`); toast.success("Deleted"); load(); };

  const downloadAd = async (sp) => {
    try {
      const res = await api.get(`/spots/${sp.id}/audio?download=1`, { responseType: "blob" });
      const ext = (res.headers["content-type"] || "").includes("wav") ? "wav" : "mp3";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(sp.business_name || "ad").replace(/[^A-Za-z0-9_-]+/g, "_")}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloading ad audio");
    } catch (e) { toast.error(e.response?.data?.detail || "No audio to download"); }
  };

  const openEditSpot = (sp) => setEditSpot({
    id: sp.id, business_name: sp.business_name || "", contact_email: sp.contact_email || "",
    duration: sp.duration || 30, plays_purchased: sp.plays_purchased || 0, plays_aired: sp.plays_aired || 0,
    link_url: sp.link_url || "", package_name: sp.package_name || "", price: sp.price || 0,
    status: sp.status || "pending_review", user_name: sp.user_name || "", rejection_reason: sp.rejection_reason || "",
    script: sp.script || "",
    start_date: sp.start_date || "", end_date: sp.end_date || "",
    frequency_count: sp.frequency_count || 0, frequency_period: sp.frequency_period || "day",
    air_start_time: sp.air_start_time || "", air_end_time: sp.air_end_time || "",
    // read-only context
    plays_remaining: sp.plays_remaining || 0, program_title: sp.program_title, generated_by_ai: sp.generated_by_ai,
    has_logo: sp.has_logo, logo_url: sp.logo_url, audio_url: sp.audio_url, created_at: sp.created_at,
  });
  const saveSpot = async () => {
    if (!editSpot.business_name.trim()) { toast.error("Business name is required"); return; }
    setSavingSpot(true);
    try {
      await api.put(`/spots/${editSpot.id}`, {
        business_name: editSpot.business_name, contact_email: editSpot.contact_email,
        duration: Number(editSpot.duration), plays_purchased: Number(editSpot.plays_purchased),
        plays_aired: Number(editSpot.plays_aired), link_url: editSpot.link_url,
        package_name: editSpot.package_name, price: Number(editSpot.price),
        status: editSpot.status, user_name: editSpot.user_name, rejection_reason: editSpot.rejection_reason,
        script: editSpot.script,
        start_date: editSpot.start_date, end_date: editSpot.end_date,
        frequency_count: Number(editSpot.frequency_count), frequency_period: editSpot.frequency_period,
        air_start_time: editSpot.air_start_time, air_end_time: editSpot.air_end_time,
      });
      toast.success("Ad spot updated");
      setEditSpot(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update spot"); }
    finally { setSavingSpot(false); }
  };

  const uploadSpotLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !editSpot) return;
    setSavingSpot(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/spots/${editSpot.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditSpot({ ...editSpot, has_logo: true, logo_url: r.data.logo_url, _logoV: Date.now() });
      toast.success("Logo updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload logo"); }
    finally { setSavingSpot(false); }
  };

  const uploadHouseAd = async () => {
    if (!houseAd.business_name || !houseFileRef.current?.files[0]) { toast.error("Name and audio file required"); return; }
    const form = new FormData();
    form.append("business_name", houseAd.business_name);
    form.append("duration", houseAd.duration || 30);
    form.append("plays", houseAd.plays || 0);
    form.append("file", houseFileRef.current.files[0]);
    if (houseLogoRef.current?.files[0]) form.append("logo", houseLogoRef.current.files[0]);
    if (houseAd.link) form.append("link_url", houseAd.link);
    try {
      await api.post("/spots", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("House ad uploaded");
      setHouseAd(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const saveStation = async () => {
    if (!stationForm.name || !stationForm.stream_url) { toast.error("Name and stream URL required"); return; }
    try {
      const payload = { ...stationForm };
      if (!payload.image_url) payload.image_url = "https://images.unsplash.com/photo-1688413708993-39da5ee59315?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
      if (stationForm.id) await api.put(`/stations/${stationForm.id}`, payload);
      else await api.post("/stations", payload);
      toast.success("Station saved");
      setStationForm(null); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const delStation = async (id) => { await api.delete(`/stations/${id}`); toast.success("Deleted"); load(); };
  const openBuilder = async () => {
    try { const r = await api.get("/stations/presets"); setPresets(r.data.presets || []); setShowBuilder(true); }
    catch { toast.error("Could not load presets"); }
  };
  const addPreset = async (p) => {
    try {
      await api.post("/stations", { name: p.name, stream_url: p.stream_url || "", genre: p.genre || "Gospel",
        description: p.description || "", image_url: p.image_url || "", frequency: p.frequency || "", external_url: p.external_url || "" });
      toast.success(`Added ${p.name}`);
      const r = await api.get("/stations/presets"); setPresets(r.data.presets || []);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const addAllPresets = async () => {
    try {
      const r = await api.post("/stations/presets/add-all");
      toast.success(`Added ${r.data.added} station${r.data.added === 1 ? "" : "s"}`);
      const pr = await api.get("/stations/presets"); setPresets(pr.data.presets || []);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add all"); }
  };
  const dragIdx = useRef(null);
  const toggleStationFeature = async (s) => {
    try { await api.post(`/stations/${s.id}/feature`, { featured: !s.featured }); toast.success(s.featured ? "Unpinned" : `Featured ${s.name}`); load(); }
    catch { toast.error("Could not update"); }
  };
  const toggleSpotlight = async (sp) => {
    try { await api.post(`/spots/${sp.id}/spotlight`, { spotlight: !sp.spotlight, tagline: sp.tagline || "" }); toast.success(sp.spotlight ? "Removed from spotlight" : `${sp.business_name} spotlighted ✨`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const saveSpotlightTagline = async (sp, tagline) => {
    try { await api.post(`/spots/${sp.id}/spotlight`, { spotlight: true, tagline }); setSpots((prev) => prev.map((x) => x.id === sp.id ? { ...x, tagline } : x)); }
    catch { toast.error("Could not save tagline"); }
  };
  const onStationDrop = async (i) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from === null || from === i) return;
    const arr = [...stations];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    setStations(arr);
    try { await api.post("/stations/reorder", { ids: arr.map((s) => s.id) }); toast.success("Order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };

  const saveProgram = async () => {
    if (!programForm.title || !programForm.start_time) { toast.error("Title and start time required"); return; }
    const { id, ...body } = programForm;
    try {
      if (id) {
        await api.put(`/programs/${id}`, body);
        toast.success("Show updated");
      } else {
        await api.post("/programs", body);
        toast.success("Show added");
      }
      setProgramForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const delProgram = async (id) => { if (!window.confirm("Delete this show?")) return; await api.delete(`/programs/${id}`); toast.success("Deleted"); load(); };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>;

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-6 h-6 text-[#5B7CFA]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Admin Console</h1>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "analytics"}>
        <TabsList className="bg-[#141C42] border border-white/5 w-full max-w-full flex flex-wrap h-auto justify-start gap-1 p-1">
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Analytics</TabsTrigger>
          <TabsTrigger value="stations" data-testid="tab-stations" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Stations</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Schedule</TabsTrigger>
          <TabsTrigger value="spots" data-testid="tab-spots" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Ad Spots</TabsTrigger>
          <TabsTrigger value="prayers" data-testid="tab-prayers" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Prayer Requests</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Events{pendingEvents.length > 0 ? ` (${pendingEvents.length})` : ""}</TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Listener Map</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Users</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Legal</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Newsletter{subscribers.length > 0 ? ` (${subscribers.length})` : ""}</TabsTrigger>
          <TabsTrigger value="devotion" data-testid="tab-devotion" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Devotion</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Social</TabsTrigger>
          <TabsTrigger value="jingles" data-testid="tab-jingles" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Jingles</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Texting{smsUnread > 0 && <span data-testid="tab-sms-badge" className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold">{smsUnread}</span>}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-6">
          <AdminAnalytics />
        </TabsContent>

        <TabsContent value="devotion" className="mt-6">
          <DevotionManager />
        </TabsContent>

        <TabsContent value="social" className="mt-6">
          <SocialMedia />
        </TabsContent>

        <TabsContent value="jingles" className="mt-6">
          <JingleStudio />
        </TabsContent>

        <TabsContent value="sms" className="mt-6">
          <SmsManager />
        </TabsContent>

        <TabsContent value="stations" className="mt-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <button onClick={() => setStationForm({ ...emptyStation })} data-testid="add-station" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
              <Plus className="w-4 h-4" /> Add Station
            </button>
            <button onClick={openBuilder} data-testid="open-builder" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5B9BFF]/20 hover:bg-[#5B9BFF]/30 text-[#AFC0FF] font-semibold transition-colors">
              <Radio className="w-4 h-4" /> Preload popular streams
            </button>
            <button onClick={checkHealth} disabled={checkingHealth} data-testid="check-health" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 font-semibold transition-colors disabled:opacity-50">
              {checkingHealth ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />} Re-check streams
            </button>
            {Object.values(health).some((v) => v === false) && (
              <span className="text-sm text-[#FF7A70] font-semibold" data-testid="health-summary">{Object.values(health).filter((v) => v === false).length} not responding</span>
            )}
          </div>
          <div className="rounded-2xl border border-white/5 bg-[#0F1943] p-4 mb-5" data-testid="rotation-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Star className={`w-4 h-4 shrink-0 ${rotation.enabled ? "fill-[#E6B34A] text-[#E6B34A]" : "text-zinc-500"}`} />
                <div className="min-w-0">
                  <p className="font-semibold text-sm">Featured rotation</p>
                  <p className="text-xs text-zinc-500">Auto-rotate the pinned station on a schedule so the spotlight stays fresh.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={rotation.cadence}
                  onChange={(e) => saveRotation({ cadence: e.target.value })}
                  disabled={!rotation.enabled}
                  data-testid="rotation-cadence"
                  className="rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A] disabled:opacity-50"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <button
                  onClick={() => saveRotation({ enabled: !rotation.enabled })}
                  data-testid="rotation-toggle"
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${rotation.enabled ? "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                >{rotation.enabled ? "On" : "Off"}</button>
              </div>
            </div>
            {rotation.enabled && rotation.next_at && (
              <p className="text-xs text-[#E6B34A] mt-3" data-testid="rotation-next">Next rotation: {fmtDateTime(rotation.next_at)}</p>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="admin-stations">
            {showBuilder && (
              <div className="md:col-span-2 rounded-2xl border border-[#5B9BFF]/30 bg-[#0F1943] p-4" data-testid="station-builder">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-display font-bold text-lg flex items-center gap-2"><Radio className="w-5 h-5 text-[#5B9BFF]" /> Popular gospel streams</p>
                  <div className="flex items-center gap-3">
                    <button onClick={addAllPresets} data-testid="add-all-presets" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors"><Plus className="w-3.5 h-3.5" /> Add all</button>
                    <button onClick={() => setShowBuilder(false)} data-testid="close-builder" className="text-sm text-zinc-400 hover:text-white">Close</button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {presets.map((p, i) => (
                    <div key={i} data-testid={`preset-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-[#141C42] border border-white/5">
                      <img src={p.image_url} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{p.name}{p.external_url && <span className="ml-1.5 text-[10px] text-[#5B9BFF] font-bold">LINK</span>}</p>
                        <p className="text-xs text-zinc-500 truncate">{p.genre} · {p.frequency || "Online"}</p>
                      </div>
                      {p.added ? (
                        <span className="shrink-0 text-xs text-emerald-300 font-bold px-2">Added</span>
                      ) : (
                        <button onClick={() => addPreset(p)} data-testid={`add-preset-${i}`} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors"><Plus className="w-3.5 h-3.5" /> Add</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(() => {
              const unresolved = alerts.filter((a) => !a.resolved);
              return unresolved.length > 0 ? (
                <div className="md:col-span-2 rounded-2xl border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-4" data-testid="station-alerts-banner">
                  <p className="text-sm font-bold text-[#FF7A70] flex items-center gap-2"><Activity className="w-4 h-4" /> Recent outages</p>
                  <p className="text-xs text-zinc-300 mt-1">{unresolved.map((a) => a.name).join(", ")} stopped responding recently. Check the streams below and update any dead links.</p>
                </div>
              ) : null;
            })()}
            {stations.map((s) => {
              const hv = health[s.id];
              return (
              <div key={s.id} draggable onDragStart={() => { dragIdx.current = stations.indexOf(s); }} onDragOver={(e) => e.preventDefault()} onDrop={() => onStationDrop(stations.indexOf(s))} data-testid={`station-row-${s.id}`} className={`flex items-center gap-3 rounded-2xl border bg-[#141C42] p-4 min-w-0 ${hv === false ? "border-[#FF3B30]/50" : "border-white/5"}`}>
                <span className="shrink-0 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300" data-testid={`drag-${s.id}`} title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
                <button onClick={() => toggleStationFeature(s)} data-testid={`feature-${s.id}`} title={s.featured ? "Unpin featured" : "Feature at top"} className="shrink-0 text-zinc-500 hover:text-[#E6B34A]"><Star className={`w-4 h-4 ${s.featured ? "fill-[#E6B34A] text-[#E6B34A]" : ""}`} /></button>
                <img src={s.image_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Radio className="w-4 h-4 text-[#E6B34A] shrink-0" /><h3 className="font-bold truncate">{s.name}</h3>
                    {hv === false ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF7A70] text-[10px] font-bold" data-testid={`health-${s.id}`}><span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30]" /> NOT RESPONDING</span>
                    ) : hv === true ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold" data-testid={`health-${s.id}`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> ONLINE</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-zinc-500 text-[10px] font-bold" data-testid={`health-${s.id}`}>CHECKING…</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{s.genre} · {s.stream_url}</p>
                </div>
                <button onClick={() => setStationForm(s)} data-testid={`edit-station-${s.id}`} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delStation(s.id)} data-testid={`del-station-${s.id}`} className="p-2 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="programs" className="mt-6">
          <button onClick={() => setProgramForm({ ...emptyProgram })} data-testid="add-program" className="mb-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
            <Plus className="w-4 h-4" /> Add Show
          </button>
          <div className="space-y-3" data-testid="admin-programs">
            {programs.map((p) => (
              <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#141C42] p-4">
                <CalendarClock className="w-5 h-5 text-[#E6B34A]" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold truncate">{p.title} <span className="text-zinc-500 font-normal text-sm">· {p.host}</span></h3>
                  <p className="text-xs text-zinc-500">{p.day} · {p.start_time}–{p.end_time}</p>
                </div>
                <button onClick={() => setProgramForm({ ...p })} data-testid={`edit-program-${p.id}`} className="p-2 rounded-lg hover:bg-[#E6B34A]/10 text-[#E6B34A]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delProgram(p.id)} data-testid={`del-program-${p.id}`} className="p-2 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="spots" className="mt-6">
          <div className="flex flex-wrap gap-3 mb-5">
            <button onClick={() => setAiAdOpen(true)} data-testid="open-ai-ad" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#E6B34A] to-[#f0c76e] text-[#0A1030] font-bold hover:opacity-90 transition-opacity">
              <Sparkles className="w-4 h-4" /> Generate Ad with AI
            </button>
            <button onClick={() => setHouseAd({ business_name: "", duration: 30, plays: 0 })} data-testid="add-house-ad" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 text-white font-bold hover:bg-white/20 transition-colors">
              <Plus className="w-4 h-4" /> Upload House Ad
            </button>
          </div>
          {(() => {
            if (perf.length === 0) return null;
            const max = perf[0].plays || 1;
            return (
              <div className="mb-6 rounded-2xl border border-white/10 bg-[#141C42] p-5" data-testid="spot-performance">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#E6B34A]" /><h3 className="font-display font-extrabold text-lg tracking-tight">Top performing ads</h3></div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center rounded-lg bg-white/5 p-0.5" data-testid="perf-range">
                      {[["all", "All time"], ["week", "This week"], ["month", "This month"]].map(([v, label]) => (
                        <button key={v} onClick={() => setPerfRange(v)} data-testid={`perf-range-${v}`}
                          className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${perfRange === v ? "bg-[#E6B34A] text-[#0A1030]" : "text-zinc-400 hover:text-white"}`}>{label}</button>
                      ))}
                    </div>
                    <button onClick={exportPerfCsv} data-testid="perf-export-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors"><Download className="w-3.5 h-3.5" /> Export CSV</button>
                    <button onClick={sendSponsorReports} data-testid="perf-email-report" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20 text-xs font-semibold transition-colors"><Send className="w-3.5 h-3.5" /> Email sponsor reports</button>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {perf.map((s, i) => (
                    <div key={s.id} data-testid={`perf-row-${s.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="w-5 text-center font-bold text-zinc-500 tabular-nums shrink-0">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-semibold text-sm">{s.business_name}</span>
                            {s.generated_by_ai && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#E6B34A]/15 text-[#E6B34A] text-[10px] font-bold"><Sparkles className="w-2.5 h-2.5" /> AI</span>}
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#34D399] to-[#E6B34A]" style={{ width: `${Math.max((s.plays / max) * 100, 5)}%` }} />
                          </div>
                        </div>
                        <span className="shrink-0 text-xs text-zinc-400 tabular-nums w-24 text-right">{s.plays} {perfRange === "all" ? `/${s.plays_purchased} plays` : perfRange === "month" ? "this month" : "this week"}</span>
                        <button onClick={() => toggleCompare(s.id)} data-testid={`perf-compare-${s.id}`} title="Compare on one chart" className={`shrink-0 p-1.5 rounded-lg text-xs font-semibold transition-colors ${compareIds.includes(s.id) ? "bg-emerald-400/20 text-emerald-400" : "bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"}`}><GitCompare className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setChartOpen(chartOpen === s.id ? null : s.id)} data-testid={`perf-chart-${s.id}`} title="View daily plays" className={`shrink-0 p-1.5 rounded-lg text-xs font-semibold transition-colors ${chartOpen === s.id ? "bg-[#E6B34A]/15 text-[#E6B34A]" : "bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"}`}><BarChart3 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => downloadSpotReport(s.id, s.business_name)} data-testid={`perf-pdf-${s.id}`} title="Download PDF report" className="shrink-0 p-1.5 rounded-lg text-xs font-semibold bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"><Download className="w-3.5 h-3.5" /></button>
                        <button onClick={() => duplicateSpot(s.id)} data-testid={`perf-dup-${s.id}`} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors"><Sparkles className="w-3 h-3" /> Duplicate</button>
                      </div>
                      {chartOpen === s.id && (
                        <div className="mt-2 ml-8 rounded-xl bg-[#0C1338] border border-white/10 p-3" data-testid={`perf-chart-panel-${s.id}`}>
                          <p className="overline text-zinc-400 mb-2">Daily plays · last {perfRange === "month" ? 30 : 14} days</p>
                          <div style={{ width: "100%", height: 140 }}>
                            <ResponsiveContainer>
                              <LineChart data={s.daily || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} interval={perfRange === "month" ? 4 : 2} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                                <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                                <RTooltip contentStyle={{ background: "#141C42", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                                <Line type="monotone" dataKey="plays" stroke="#E6B34A" strokeWidth={2} dot={{ r: 2, fill: "#E6B34A" }} activeDot={{ r: 4 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {compareIds.length === 2 && (() => {
                  const a = perf.find((p) => p.id === compareIds[0]);
                  const b = perf.find((p) => p.id === compareIds[1]);
                  if (!a || !b) return null;
                  const merged = (a.daily || []).map((d, idx) => ({ date: d.date, a: d.plays, b: (b.daily || [])[idx]?.plays ?? 0 }));
                  return (
                    <div className="mt-3 rounded-xl bg-[#0C1338] border border-emerald-400/30 p-3" data-testid="perf-compare-panel">
                      <div className="flex items-center justify-between mb-2">
                        <p className="overline text-emerald-400">Compare · daily plays ({perfRange === "month" ? 30 : 14} days)</p>
                        <button onClick={() => setCompareIds([])} data-testid="perf-compare-clear" className="text-xs text-zinc-400 hover:text-white">Clear</button>
                      </div>
                      <div className="flex flex-wrap gap-4 mb-1 text-xs">
                        <span className="flex items-center gap-1.5 text-zinc-300"><span className="w-3 h-0.5 bg-[#E6B34A] inline-block" /> {a.business_name}</span>
                        <span className="flex items-center gap-1.5 text-zinc-300"><span className="w-3 h-0.5 bg-[#38BDF8] inline-block" /> {b.business_name}</span>
                      </div>
                      <div style={{ width: "100%", height: 160 }}>
                        <ResponsiveContainer>
                          <LineChart data={merged} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} interval={perfRange === "month" ? 4 : 2} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                            <RTooltip contentStyle={{ background: "#141C42", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                            <Line type="monotone" dataKey="a" name={a.business_name} stroke="#E6B34A" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="b" name={b.business_name} stroke="#38BDF8" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })()}
                {reportCfg && (
                  <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2 text-sm" data-testid="report-schedule">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    <label className="flex items-center gap-1.5 text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={reportCfg.enabled} onChange={(e) => saveReportCfg({ enabled: e.target.checked })} className="accent-[#E6B34A] w-4 h-4" data-testid="report-enabled" />
                      Auto-email sponsor reports weekly on
                    </label>
                    <select value={reportCfg.send_day} onChange={(e) => saveReportCfg({ send_day: Number(e.target.value) })} data-testid="report-day" disabled={!reportCfg.enabled} className="rounded-md bg-[#1E2A5A] border border-white/10 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A] disabled:opacity-50">
                      {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
                    </select>
                    <span className="text-zinc-400">at</span>
                    <select value={reportCfg.send_hour} onChange={(e) => saveReportCfg({ send_hour: Number(e.target.value) })} data-testid="report-hour" disabled={!reportCfg.enabled} className="rounded-md bg-[#1E2A5A] border border-white/10 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A] disabled:opacity-50">
                      {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                    </select>
                    <span className="text-zinc-500 text-xs">UTC</span>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <span className="text-zinc-400 text-xs">CC me:</span>
                      <input type="email" defaultValue={reportCfg.cc_email || ""} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (reportCfg.cc_email || "")) saveReportCfg({ cc_email: v }); }} placeholder="you@church.org" data-testid="report-cc" className="w-44 rounded-md bg-[#1E2A5A] border border-white/10 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {spots.length === 0 ? (
            <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="admin-spots-empty">No ad spots yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="admin-spots">
              {spots.map((sp) => (
                <div key={sp.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 min-w-0" data-testid={`admin-spot-${sp.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {sp.has_logo && (
                        <img src={`${API}/spots/${sp.id}/logo?auth=${localStorage.getItem("token")}`} alt="Sponsor logo" className="w-10 h-10 rounded-lg object-cover border border-white/10 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#E6B34A]" /><h3 className="font-bold truncate">{sp.business_name}</h3></div>
                        <p className="text-xs text-zinc-500 mt-0.5">{sp.package_name} · {sp.duration}s · {sp.plays_purchased} plays · by {sp.user_name}</p>
                      </div>
                    </div>
                    <StatusBadge status={sp.status} />
                  </div>

                  <SpotAudioPreview spot={sp} />

                  {sp.airing && <AiringBadge airing={sp.airing} />}

                  {sp.status === "pending_review" && (
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => reviewSpot(sp.id, "approve")} data-testid={`approve-${sp.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20 text-sm font-semibold transition-colors"><Check className="w-4 h-4" /> Approve</button>
                      <button onClick={() => reviewSpot(sp.id, "reject", "Did not meet content guidelines")} data-testid={`reject-spot-${sp.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 text-sm font-semibold transition-colors"><X className="w-4 h-4" /> Reject</button>
                    </div>
                  )}

                  {(sp.status === "approved" || sp.status === "scheduled") && (
                    <div className="mt-4">
                      <label className="overline text-zinc-400">Schedule into program</label>
                      <select
                        value={sp.program_id || ""}
                        onChange={(e) => scheduleSpot(sp.id, e.target.value)}
                        data-testid={`schedule-${sp.id}`}
                        className="mt-1.5 w-full rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]"
                      >
                        <option value="">— Not scheduled —</option>
                        {programs.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.day} {p.start_time}</option>)}
                      </select>
                    </div>
                  )}

                  {(sp.status === "approved" || sp.status === "scheduled") && (
                    <div className={`mt-4 rounded-xl border p-3 ${sp.spotlight ? "border-[#E6B34A]/60 bg-[#E6B34A]/5" : "border-white/5 bg-white/[0.02]"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Star className={`w-4 h-4 shrink-0 ${sp.spotlight ? "fill-[#E6B34A] text-[#E6B34A]" : "text-zinc-500"}`} />
                          <span className="text-sm font-semibold truncate">{sp.spotlight ? "In the Sponsor Spotlight" : "Sponsor Spotlight"}</span>
                        </div>
                        <button onClick={() => toggleSpotlight(sp)} data-testid={`spotlight-${sp.id}`} className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${sp.spotlight ? "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>{sp.spotlight ? "Remove pin" : "Pin to top"}</button>
                      </div>
                      {sp.spotlight && (
                        <Input
                          defaultValue={sp.tagline || ""}
                          onBlur={(e) => saveSpotlightTagline(sp, e.target.value.trim())}
                          placeholder="Optional tagline shown to listeners (e.g. Family-owned bakery in Atlanta)"
                          data-testid={`spotlight-tagline-${sp.id}`}
                          className="mt-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm"
                        />
                      )}
                    </div>
                  )}

                  <div className="flex items-center mt-4 pt-3 border-t border-white/5">
                    {sp.original_filename && <span className="text-xs text-zinc-500 truncate">{sp.original_filename}</span>}
                    {sp.audio_url && <button onClick={() => downloadAd(sp)} data-testid={`download-spot-${sp.id}`} title="Download ad audio" className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors"><Download className="w-3.5 h-3.5" /> Download</button>}
                    <button onClick={() => openEditSpot(sp)} data-testid={`edit-spot-${sp.id}`} title="Edit ad spot" className={`${sp.audio_url ? "ml-2" : "ml-auto"} inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => duplicateSpot(sp.id)} data-testid={`dup-spot-${sp.id}`} title="Duplicate as AI draft" className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-semibold transition-colors"><Sparkles className="w-3.5 h-3.5" /> Duplicate</button>
                    <button onClick={() => delSpot(sp.id)} data-testid={`del-adminspot-${sp.id}`} className="ml-2 p-2 rounded-lg hover:bg-[#FF3B30]/10 text-zinc-500 hover:text-[#FF3B30] transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="prayers" className="mt-6">
          {prayers.length === 0 ? (
            <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="admin-prayers-empty">No prayer requests yet.</p>
          ) : (
            <div className="space-y-3" data-testid="admin-prayers">
              {prayers.map((p) => (
                <div key={p.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid={`admin-prayer-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <HandHeart className="w-5 h-5 text-[#E6B34A] shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-zinc-200">{p.text}</p>
                      <p className="text-xs text-zinc-500 mt-2">
                        {p.anonymous ? "Anonymous" : (p.user_name || "Member")}
                        {p.created_at && <span> · {fmtDateTime(p.created_at)}</span>}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      {p.status === "prayed" ? (
                        <span className="overline px-2.5 py-1 rounded-full text-emerald-400 bg-emerald-400/10">Prayed 🙏</span>
                      ) : (
                        <button onClick={() => markPrayed(p.id)} data-testid={`mark-prayed-${p.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E6B34A]/10 text-[#E6B34A] hover:bg-[#E6B34A]/20 text-xs font-semibold transition-colors">
                          <Check className="w-3.5 h-3.5" /> Mark prayed
                        </button>
                      )}
                      <button onClick={() => delPrayer(p.id)} data-testid={`admin-prayer-del-${p.id}`} title="Delete" className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          {pendingEvents.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center border border-dashed border-white/10 rounded-2xl mb-8" data-testid="admin-events-empty">No events awaiting review.</p>
          ) : (
            <>
            <h3 className="font-display font-bold text-lg mb-3">Awaiting review ({pendingEvents.length})</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="admin-pending-events">
              {pendingEvents.map((ev) => (
                <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="rounded-2xl border border-white/5 bg-[#141C42] p-4 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-zinc-300">{ev.event_type}</span>
                    <span className="text-xs text-zinc-500">by {ev.created_by_name || "user"}</span>
                  </div>
                  <h3 className="font-bold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#E6B34A]" /> {ev.title}</h3>
                  <p className="text-xs text-zinc-400 mt-1">{fmtDateTime(ev.starts_at)} · {[ev.venue, ev.city, ev.country].filter(Boolean).join(", ")}</p>
                  {ev.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{ev.description}</p>}
                  {ev.link && <a href={ev.link} target="_blank" rel="noreferrer" className="text-xs text-[#8CA6FF] hover:underline mt-1 inline-block break-all">{ev.link}</a>}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => reviewEvent(ev.id, "approve")} data-testid={`admin-event-approve-${ev.id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/15 text-emerald-300 font-semibold text-sm hover:bg-emerald-500/25 transition-colors">
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => reviewEvent(ev.id, "reject")} data-testid={`admin-event-reject-${ev.id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#FF3B30]/15 text-[#FF7A70] font-semibold text-sm hover:bg-[#FF3B30]/25 transition-colors">
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setEditEvent(ev)} data-testid={`admin-event-edit-${ev.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 text-xs font-semibold transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => delEvent(ev.id)} data-testid={`admin-event-del-${ev.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs font-semibold transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          <h3 className="font-display font-bold text-lg mb-3">Published events — pin to home</h3>
          {approvedEvents.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center border border-dashed border-white/10 rounded-2xl" data-testid="admin-approved-empty">No published events yet.</p>
          ) : (
            <div className="space-y-3" data-testid="admin-approved-events">
              {approvedEvents.map((ev) => (
                <div key={ev.id} data-testid={`admin-approved-${ev.id}`} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#141C42] p-4">
                  <CalendarDays className="w-5 h-5 text-[#E6B34A] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold truncate">{ev.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{fmtDateTime(ev.starts_at)} · {[ev.city, ev.country].filter(Boolean).join(", ")} · {ev.attendee_count} going</p>
                  </div>
                  <button
                    onClick={() => toggleFeature(ev)}
                    data-testid={`admin-feature-${ev.id}`}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors shrink-0 ${ev.featured ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                  >
                    <Star className="w-4 h-4" fill={ev.featured ? "currentColor" : "none"} /> {ev.featured ? "Featured" : "Feature"}
                  </button>
                  <button onClick={() => setEditEvent(ev)} data-testid={`admin-approved-edit-${ev.id}`} title="Edit" className="p-2 rounded-xl bg-white/5 text-zinc-300 hover:bg-white/10 transition-colors shrink-0"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delEvent(ev.id)} data-testid={`admin-approved-del-${ev.id}`} title="Delete" className="p-2 rounded-xl bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="map" className="mt-6">
          <ListenerMap />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-[#E6B34A]" />
              <h3 className="font-display font-bold text-lg">Users ({users.length})</h3>
            </div>
            <button onClick={() => setUserForm({ ...emptyUser })} data-testid="add-user-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold text-sm hover:bg-[#CF9A34] transition-colors">
              <UserPlus className="w-4 h-4" /> Add user
            </button>
          </div>
          <div className="space-y-3" data-testid="admin-users">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#141C42] p-4 min-w-0">
                <div className="w-11 h-11 rounded-full bg-[#1E2A5A] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                  {u.avatar_url ? <img src={`${API}${u.avatar_url.replace('/api', '')}`} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-sm text-zinc-400">{(u.name || u.email || "?").charAt(0).toUpperCase()}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold truncate">{u.title ? `${u.title} ` : ""}{u.name || "—"}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ROLE_STYLES[u.role] || ROLE_STYLES.user}`}>{u.role}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{u.email}{[u.city, u.state].filter(Boolean).length ? ` · ${[u.city, u.state].filter(Boolean).join(", ")}` : ""}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {me?.id !== u.id && (
                    <button onClick={() => doImpersonate(u)} data-testid={`impersonate-${u.id}`} title="View as this user" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:text-[#E6B34A] hover:bg-white/10"><Eye className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => resetUserPassword(u)} data-testid={`reset-pw-${u.id}`} title="Reset password" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:text-[#E6B34A] hover:bg-white/10"><KeyRound className="w-4 h-4" /></button>
                  <button onClick={() => setUserForm({ id: u.id, name: u.name || "", email: u.email, password: "", role: u.role, title: u.title || "", phone: u.phone || "", birth_date: u.birth_date || "", city: u.city || "", state: u.state || "", country: u.country || "United States" })} data-testid={`edit-user-${u.id}`} title="Edit" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                  {me?.id !== u.id && (
                    <button onClick={() => delUser(u)} data-testid={`del-user-${u.id}`} title="Delete" className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="legal" className="mt-6">
          <div className="max-w-2xl space-y-6" data-testid="admin-legal">
            <div className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[#E6B34A]" />
              <h3 className="font-display font-bold text-lg">Terms & Privacy</h3>
            </div>
            <p className="text-sm text-zinc-400">Edit the content shown on the public Terms &amp; Conditions and Privacy Policy pages. Basic Markdown (#, ##, -, **bold**, _italic_) is supported.</p>
            <div>
              <label className="overline text-zinc-400">Terms &amp; Conditions</label>
              <Textarea value={legal.terms} onChange={(e) => setLegal({ ...legal, terms: e.target.value })} data-testid="legal-terms" rows={12} className="mt-1.5 bg-[#1E2A5A] border-white/10 font-mono text-xs" />
            </div>
            <div>
              <label className="overline text-zinc-400">Privacy Policy</label>
              <Textarea value={legal.privacy} onChange={(e) => setLegal({ ...legal, privacy: e.target.value })} data-testid="legal-privacy" rows={12} className="mt-1.5 bg-[#1E2A5A] border-white/10 font-mono text-xs" />
            </div>
            <button onClick={saveLegal} disabled={savingLegal} data-testid="save-legal" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
              {savingLegal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save legal pages"}
            </button>
          </div>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-6">
          <div className="max-w-3xl space-y-5" data-testid="admin-newsletter">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#E6B34A]" />
                <h3 className="font-display font-bold text-lg">Newsletter subscribers</h3>
                <span className="text-sm text-zinc-500">({subscribers.length})</span>
              </div>
              <button onClick={exportSubscribersCsv} disabled={subscribers.length === 0} data-testid="export-newsletter-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#141C42] p-5 space-y-3" data-testid="blast-compose">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-[#E6B34A]" />
                <h4 className="font-display font-bold">Send an email blast</h4>
              </div>
              <p className="text-xs text-zinc-500">Send a devotional or event announcement to all {subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}.</p>
              <Input value={blast.subject} onChange={(e) => setBlast({ ...blast, subject: e.target.value })} placeholder="Subject (e.g. This Sunday's service)" data-testid="blast-subject" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              <div className="flex flex-wrap items-center gap-2" data-testid="blast-segment">
                <span className="overline text-zinc-400">Audience</span>
                <select value={segment.type} onChange={(e) => setSegment({ type: e.target.value, value: "" })} data-testid="segment-type" className="rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="all">All subscribers</option>
                  <option value="rsvps">Event RSVPs</option>
                  <option value="city">By city</option>
                </select>
                {segment.type === "city" && (
                  <Input value={segment.value} onChange={(e) => setSegment({ ...segment, value: e.target.value })} placeholder="City name" data-testid="segment-city" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] max-w-[180px]" />
                )}
                {segCount !== null && <span className="text-xs text-[#E6B34A] font-semibold" data-testid="segment-count">{segCount} recipient{segCount === 1 ? "" : "s"}</span>}
              </div>
              <div className="rounded-md border border-white/10 bg-[#1E2A5A] overflow-hidden">
                <div className="flex items-center gap-1 border-b border-white/10 px-2 py-1.5">
                  <button type="button" onClick={() => exec("bold")} data-testid="blast-bold" className="px-2.5 py-1 rounded font-bold text-sm hover:bg-white/10" title="Bold">B</button>
                  <button type="button" onClick={() => exec("italic")} data-testid="blast-italic" className="px-2.5 py-1 rounded italic text-sm hover:bg-white/10" title="Italic">i</button>
                  <button type="button" onClick={execLink} data-testid="blast-link" className="px-2.5 py-1 rounded text-sm hover:bg-white/10 inline-flex items-center gap-1" title="Insert link"><LinkIcon className="w-3.5 h-3.5" /> Link</button>
                  <button type="button" onClick={execImage} data-testid="blast-image" className="px-2.5 py-1 rounded text-sm hover:bg-white/10 inline-flex items-center gap-1" title="Insert image"><ImageIcon className="w-3.5 h-3.5" /> Image</button>
                </div>
                <div ref={blastEditorRef} contentEditable suppressContentEditableWarning data-testid="blast-editor" className="min-h-[120px] px-3 py-2 text-sm focus:outline-none [&_a]:text-[#E6B34A] [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2" />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={blast.sendLater} onChange={(e) => setBlast({ ...blast, sendLater: e.target.checked })} data-testid="blast-schedule-toggle" className="accent-[#E6B34A]" />
                Schedule for later
              </label>
              {blast.sendLater && (
                <Input type="datetime-local" value={blast.sendAt} onChange={(e) => setBlast({ ...blast, sendAt: e.target.value })} data-testid="blast-datetime" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] max-w-xs" />
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button onClick={() => setPreviewOpen(true)} data-testid="blast-preview" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 font-semibold hover:bg-white/10 transition-colors"><Eye className="w-4 h-4" /> Preview</button>
                <button onClick={() => doSend({ test: true })} disabled={sendingBlast} data-testid="blast-test" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 font-semibold hover:bg-white/10 transition-colors disabled:opacity-50"><Send className="w-4 h-4" /> Send test to me</button>
                <button onClick={() => doSend({ test: false })} disabled={sendingBlast || subscribers.length === 0} data-testid="send-blast" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
                  {sendingBlast ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {blast.sendLater ? "Schedule blast" : "Send blast"}
                </button>
              </div>
              {scheduled.length > 0 && (
                <div className="pt-2 border-t border-white/10" data-testid="scheduled-list">
                  <p className="overline text-zinc-400 mb-2">Scheduled</p>
                  {scheduled.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                      <span className="truncate"><span className="font-semibold">{s.subject}</span> <span className="text-zinc-500">· {fmtDateTime(s.send_at)}</span></span>
                      <button onClick={() => cancelScheduled(s.id)} data-testid={`cancel-scheduled-${s.id}`} className="shrink-0 text-[#FF3B30] hover:underline text-xs font-semibold">Cancel</button>
                    </div>
                  ))}
                </div>
              )}
              {history.length > 0 && (
                <div className="pt-2 border-t border-white/10" data-testid="blast-history">
                  <p className="overline text-zinc-400 mb-2">Recent blasts</p>
                  {history.map((h) => (
                    <button key={h.id} onClick={() => openBlastDetail(h.id)} data-testid={`history-${h.id}`} className="w-full flex items-center justify-between gap-3 py-1.5 text-sm text-left hover:bg-white/5 rounded-lg px-2 -mx-2 transition-colors">
                      <span className="truncate"><span className="font-semibold">{h.subject}</span> <span className="text-zinc-500">· {fmtDate(h.sent_at)} · {h.segment}</span></span>
                      <span className="shrink-0 text-xs text-[#E6B34A] font-semibold">{h.opens}/{h.recipients} opened · {h.clicks} clicks</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="bg-[#0C1338] border-white/10 text-white max-w-lg" data-testid="blast-preview-dialog">
                <DialogHeader><DialogTitle className="font-display font-extrabold text-xl">Email preview</DialogTitle></DialogHeader>
                <div className="rounded-xl overflow-hidden">
                  <div style={{ fontFamily: "Arial, sans-serif", background: "#0C1338", color: "#e8e8ef", padding: 24, borderRadius: 12 }}>
                    <h1 style={{ color: "#E6B34A", fontSize: 20, margin: "0 0 12px" }}>In His Image Radio</h1>
                    <div className="text-sm leading-relaxed [&_a]:text-[#E6B34A] [&_img]:max-w-full [&_img]:rounded-lg" dangerouslySetInnerHTML={{ __html: getBlastHtml() || "<p style='color:#8a8aa0'>Your message will appear here…</p>" }} />
                    <p style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #26305e", fontSize: 12, color: "#8a8aa0" }}>You are receiving this because you subscribed to In His Image Radio updates. <span style={{ color: "#E6B34A" }}>Unsubscribe</span> with one click.</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={!!blastDetail} onOpenChange={(o) => !o && setBlastDetail(null)}>
              <DialogContent className="bg-[#0C1338] border-white/10 text-white max-w-lg" data-testid="blast-detail-dialog">
                {blastDetail && (
                  <>
                    <DialogHeader><DialogTitle className="font-display font-extrabold text-xl">{blastDetail.subject}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-3 gap-2 text-center my-2">
                      <div className="rounded-xl bg-white/5 p-3"><p className="font-display font-black text-2xl">{blastDetail.recipients}</p><p className="text-[11px] text-zinc-400">Recipients</p></div>
                      <div className="rounded-xl bg-white/5 p-3"><p className="font-display font-black text-2xl text-[#E6B34A]">{blastDetail.opens}</p><p className="text-[11px] text-zinc-400">Opened</p></div>
                      <div className="rounded-xl bg-white/5 p-3"><p className="font-display font-black text-2xl text-[#34D399]">{blastDetail.clicks}</p><p className="text-[11px] text-zinc-400">Clicked</p></div>
                    </div>
                    {blastDetail.top_links.length > 0 && (() => {
                      const links = blastDetail.top_links.slice(0, 4);
                      const maxClicks = Math.max(...links.map((l) => l.clicks), 1);
                      const totalClicks = links.reduce((s, l) => s + l.clicks, 0) || 1;
                      return (
                        <div className="text-sm" data-testid="click-heatmap">
                          <p className="overline text-zinc-400 mb-1.5">Click heatmap · top links</p>
                          <div className="space-y-2">
                            {links.map((l, i) => (
                              <div key={i} data-testid={`heatmap-bar-${i}`} className="relative rounded-lg bg-white/5 overflow-hidden">
                                <div className="absolute inset-y-0 left-0 rounded-lg bg-gradient-to-r from-[#34D399]/70 to-[#E6B34A]/70 transition-all duration-500" style={{ width: `${Math.max((l.clicks / maxClicks) * 100, 6)}%` }} />
                                <div className="relative flex items-center justify-between gap-2 px-2.5 py-1.5">
                                  <span className="truncate text-white text-xs font-medium">{l.url}</span>
                                  <span className="shrink-0 text-xs font-bold text-white tabular-nums">{l.clicks} <span className="text-white/70 font-medium">· {Math.round((l.clicks / totalClicks) * 100)}%</span></span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="text-sm max-h-40 overflow-y-auto mt-1">
                      <p className="overline text-zinc-400 mb-1">Didn't open ({blastDetail.non_openers.length})</p>
                      {blastDetail.non_openers.length === 0 ? <p className="text-zinc-500 text-xs">Everyone opened it! 🎉</p> :
                        blastDetail.non_openers.map((e, i) => <p key={i} className="text-zinc-400 text-xs truncate">{e}</p>)}
                    </div>
                    <button onClick={resendUnopened} disabled={blastDetail.non_openers.length === 0} data-testid="resend-unopened" className="w-full mt-2 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
                      Re-send to {blastDetail.non_openers.length} who didn't open
                    </button>
                  </>
                )}
              </DialogContent>
            </Dialog>

            {subscribers.length === 0 ? (
              <p className="text-sm text-zinc-500 rounded-2xl border border-white/5 bg-[#141C42] p-6">No one has signed up for the newsletter yet.</p>
            ) : (
              <div className="rounded-2xl border border-white/5 bg-[#141C42] overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5 text-left text-zinc-400">
                      <th className="px-4 py-3 overline font-normal">Email</th>
                      <th className="px-4 py-3 overline font-normal">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody data-testid="newsletter-list">
                    {subscribers.map((s, i) => (
                      <tr key={i} className="border-b border-white/5 last:border-0">
                        <td className="px-4 py-3 font-medium break-all">{s.email}</td>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{s.created_at ? fmtDate(s.created_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="max-w-2xl rounded-2xl border border-white/5 bg-[#141C42] p-6 space-y-5" data-testid="admin-settings">
            <div>
              <h3 className="font-display font-bold text-lg mb-1">Homepage & branding</h3>
              <p className="text-sm text-zinc-400">Edit the words, colors and logo shown across the app.</p>
            </div>

            <div>
              <label className="overline text-zinc-400">Hero heading</label>
              <Input value={settings.hero_heading || ""} onChange={(e) => setSettings({ ...settings, hero_heading: e.target.value })} data-testid="setting-hero-heading" className="mt-1.5 bg-[#1E2A5A] border-white/10" />
            </div>
            <div>
              <label className="overline text-zinc-400">Highlighted word (colored part of the heading)</label>
              <Input value={settings.hero_highlight || ""} onChange={(e) => setSettings({ ...settings, hero_highlight: e.target.value })} data-testid="setting-hero-highlight" className="mt-1.5 bg-[#1E2A5A] border-white/10" />
            </div>
            <div>
              <label className="overline text-zinc-400">Hero subtitle</label>
              <Textarea value={settings.hero_subtitle || ""} onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })} data-testid="setting-hero-subtitle" rows={3} className="mt-1.5 bg-[#1E2A5A] border-white/10" />
            </div>

            {/* Live preview */}
            <div data-testid="brand-preview">
              <label className="overline text-zinc-400">Live preview</label>
              <div className="mt-1.5 rounded-2xl overflow-hidden border border-white/10 p-5 bg-[#0A1030]">
                <div className="flex items-center gap-3 mb-4">
                  <img src={settings.has_logo && settings.logo_url ? `${API}${settings.logo_url.replace("/api", "")}?v=${logoVer}` : "/logo-transparent.png"} alt="" className="w-12 h-12 object-contain" />
                  <span className="font-display font-extrabold text-lg">In His Image Radio</span>
                </div>
                <h2 className="font-display font-black text-2xl tracking-tighter leading-tight">
                  {(settings.hero_heading || "").split(settings.hero_highlight || "\u0000").map((part, i, arr) => (
                    <React.Fragment key={i}>{part}{i < arr.length - 1 && <span style={{ color: settings.brand_primary }}>{settings.hero_highlight}</span>}</React.Fragment>
                  ))}
                </h2>
                <p className="text-sm text-zinc-400 mt-2">{settings.hero_subtitle}</p>
                <span className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[#0A1030] font-bold text-sm" style={{ backgroundColor: settings.brand_primary }}>{settings.hero_cta}</span>
              </div>
            </div>

            <div className="border-t border-white/5 pt-5">
              <h4 className="font-display font-bold mb-1">Guest welcome card</h4>
              <p className="text-sm text-zinc-400 mb-3">The box that greets signed-out visitors.</p>
              <div className="space-y-3">
                <div>
                  <label className="overline text-zinc-400">Card title</label>
                  <Input value={settings.guest_card_title || ""} onChange={(e) => setSettings({ ...settings, guest_card_title: e.target.value })} data-testid="setting-guest-title" className="mt-1.5 bg-[#1E2A5A] border-white/10" />
                </div>
                <div>
                  <label className="overline text-zinc-400">Card highlighted word</label>
                  <Input value={settings.guest_card_highlight || ""} onChange={(e) => setSettings({ ...settings, guest_card_highlight: e.target.value })} data-testid="setting-guest-highlight" className="mt-1.5 bg-[#1E2A5A] border-white/10" />
                </div>
                <div>
                  <label className="overline text-zinc-400">Card body</label>
                  <Textarea value={settings.guest_card_body || ""} onChange={(e) => setSettings({ ...settings, guest_card_body: e.target.value })} data-testid="setting-guest-body" rows={2} className="mt-1.5 bg-[#1E2A5A] border-white/10" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline text-zinc-400">"Start Listening" button text</label>
                <Input value={settings.hero_cta || ""} onChange={(e) => setSettings({ ...settings, hero_cta: e.target.value })} data-testid="setting-hero-cta" className="mt-1.5 bg-[#1E2A5A] border-white/10" />
              </div>
              <div>
                <label className="overline text-zinc-400">Brand accent color</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="color" value={settings.brand_primary || "#E6B34A"} onChange={(e) => setSettings({ ...settings, brand_primary: e.target.value })} data-testid="setting-brand-color" className="w-12 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                  <Input value={settings.brand_primary || ""} onChange={(e) => setSettings({ ...settings, brand_primary: e.target.value })} className="bg-[#1E2A5A] border-white/10 font-mono" />
                </div>
              </div>
            </div>

            <div>
              <label className="overline text-zinc-400">App logo</label>
              <div className="flex items-center gap-4 mt-1.5">
                <div className="w-16 h-16 rounded-xl bg-[#0C1338] border border-white/10 flex items-center justify-center overflow-hidden">
                  <img src={settings.has_logo && settings.logo_url ? `${API}${settings.logo_url.replace("/api", "")}?v=${Date.now()}` : "/logo-transparent.png"} alt="logo" className="w-full h-full object-contain" />
                </div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="setting-logo-file" onChange={(e) => uploadLogo(e.target.files[0])} />
                <button onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="setting-logo-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors disabled:opacity-60">
                  {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload logo
                </button>
                {settings.has_logo && <button onClick={resetLogo} data-testid="setting-logo-reset" className="text-sm text-zinc-500 hover:text-[#FF3B30]">Reset to default</button>}
              </div>
            </div>

            <div className="border-t border-white/5 pt-5" data-testid="host-editor">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-display font-bold">Home hero hosts</h4>
                <button onClick={addHost} data-testid="host-add" className="text-sm font-semibold" style={{ color: "#E6B34A" }}>+ Add host</button>
              </div>
              <p className="text-sm text-zinc-400 mb-3">Feature different hosts on the home page by day of week.</p>
              <div className="space-y-3">
                {hosts.map((h, i) => (
                  <div key={i} data-testid={`host-row-${i}`} className="rounded-xl border border-white/10 bg-[#0C1338] p-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-[#1E2A5A] border border-white/10 flex items-center justify-center shrink-0">
                        {h.photo ? <img src={h.photo.startsWith("http") ? h.photo : `${API.replace("/api", "")}${h.photo}`} alt="" className="w-full h-full object-cover" /> : <Radio className="w-5 h-5 text-zinc-600" />}
                      </div>
                      <button onClick={() => hostPhotoRefs.current[i]?.click()} data-testid={`host-photo-upload-${i}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold transition-colors">
                        {uploadingHost === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photo
                      </button>
                      <input ref={(el) => (hostPhotoRefs.current[i] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => uploadHostPhoto(i, e.target.files[0])} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input value={h.name} onChange={(e) => updateHost(i, { name: e.target.value })} placeholder="Host name" data-testid={`host-name-${i}`} className="bg-[#1E2A5A] border-white/10" />
                      <Input value={h.role} onChange={(e) => updateHost(i, { role: e.target.value })} placeholder="Role · Show" className="bg-[#1E2A5A] border-white/10" />
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {DAY_LABELS.map((d, di) => (
                        <button key={di} onClick={() => toggleDay(i, di)} data-testid={`host-${i}-day-${di}`} className={`text-[11px] px-2 py-1 rounded-full font-semibold transition-colors ${h.days.includes(di) ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-400"}`}>{d}</button>
                      ))}
                      <button onClick={() => removeHost(i)} data-testid={`host-remove-${i}`} className="ml-auto text-zinc-500 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={saveHosts} data-testid="save-hosts" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors">Save host lineup</button>
            </div>

            <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Station dialog */}
      <Dialog open={!!stationForm} onOpenChange={(o) => !o && setStationForm(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="station-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{stationForm?.id ? "Edit" : "New"} Station</DialogTitle></DialogHeader>
          {stationForm && (
            <div className="space-y-3 mt-2">
              {[["name", "Station name"], ["stream_url", "Stream URL (mp3/aac)"], ["genre", "Genre"], ["frequency", "Frequency / label"], ["image_url", "Image URL"]].map(([k, label]) => (
                <div key={k}>
                  <label className="overline text-zinc-400">{label}</label>
                  <Input value={stationForm[k]} onChange={(e) => setStationForm({ ...stationForm, [k]: e.target.value })} data-testid={`sf-${k}`} className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
                </div>
              ))}
              <div>
                <label className="overline text-zinc-400">Ad frequency — seconds of playback between ads</label>
                <Input type="number" min="15" value={stationForm.ad_interval ?? 180} onChange={(e) => setStationForm({ ...stationForm, ad_interval: Number(e.target.value) })} data-testid="sf-ad_interval" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
                <p className="text-xs text-zinc-500 mt-1">Lower = ads play more often. e.g. 180 = every 3 minutes.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Description</label>
                <Textarea value={stationForm.description} onChange={(e) => setStationForm({ ...stationForm, description: e.target.value })} data-testid="sf-description" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <button onClick={saveStation} data-testid="save-station" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Save Station</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Program dialog */}
      <Dialog open={!!programForm} onOpenChange={(o) => !o && setProgramForm(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="program-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{programForm?.id ? "Edit Show" : "New Show"}</DialogTitle></DialogHeader>
          {programForm && (
            <div className="space-y-3 mt-2">
              <div><label className="overline text-zinc-400">Title</label><Input value={programForm.title} onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })} data-testid="pf-title" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div><label className="overline text-zinc-400">Host</label><Input value={programForm.host} onChange={(e) => setProgramForm({ ...programForm, host: e.target.value })} data-testid="pf-host" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div>
                <label className="overline text-zinc-400">Link to host profile (optional)</label>
                <select
                  value={programForm.host_id || ""}
                  onChange={(e) => {
                    const hid = e.target.value;
                    const h = users.find((u) => u.id === hid);
                    setProgramForm({ ...programForm, host_id: hid, host: hid && h ? (h.name || programForm.host) : programForm.host });
                  }}
                  data-testid="pf-host-id"
                  className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]"
                >
                  <option value="">— No linked profile —</option>
                  {users.filter((u) => u.role === "host" || u.role === "admin").map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Listeners can tap the host name on the schedule to open their profile.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Station to play</label>
                <select value={programForm.station_id || ""} onChange={(e) => setProgramForm({ ...programForm, station_id: e.target.value })} data-testid="pf-station" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="">— No station —</option>
                  {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Listeners can tap the show to tune into this station.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Day</label>
                <select value={programForm.day} onChange={(e) => setProgramForm({ ...programForm, day: e.target.value })} data-testid="pf-day" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Start</label><Input type="time" value={programForm.start_time} onChange={(e) => setProgramForm({ ...programForm, start_time: e.target.value })} data-testid="pf-start" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">End</label><Input type="time" value={programForm.end_time} onChange={(e) => setProgramForm({ ...programForm, end_time: e.target.value })} data-testid="pf-end" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div><label className="overline text-zinc-400">Description</label><Textarea value={programForm.description} onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })} data-testid="pf-desc" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2.5" data-testid="pf-takes-calls-row">
                <input type="checkbox" checked={!!programForm.takes_calls} onChange={(e) => setProgramForm({ ...programForm, takes_calls: e.target.checked })} data-testid="pf-takes-calls" className="accent-[#E6B34A] w-4 h-4" />
                <PhoneCall className="w-4 h-4 text-[#E6B34A]" /> This show takes calls <span className="text-[11px] text-zinc-500">(auto-opens the call-in line)</span>
              </label>
              <button onClick={saveProgram} data-testid="save-program" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">{programForm?.id ? "Save Changes" : "Add Show"}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* House ad dialog */}
      <AiAdGenerator open={aiAdOpen} onClose={() => { setAiAdOpen(false); setCloneDraft(null); }} onCreated={load} initialDraft={cloneDraft} onInitialConsumed={() => setCloneDraft(null)} />
      <Dialog open={!!houseAd} onOpenChange={(o) => !o && setHouseAd(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="house-ad-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Upload House Ad</DialogTitle></DialogHeader>
          {houseAd && (
            <div className="space-y-3 mt-2">
              <div><label className="overline text-zinc-400">Business / sponsor name</label><Input value={houseAd.business_name} onChange={(e) => setHouseAd({ ...houseAd, business_name: e.target.value })} data-testid="ha-name" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Duration (s)</label><Input type="number" value={houseAd.duration} onChange={(e) => setHouseAd({ ...houseAd, duration: e.target.value })} data-testid="ha-duration" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Plays</label><Input type="number" value={houseAd.plays} onChange={(e) => setHouseAd({ ...houseAd, plays: e.target.value })} data-testid="ha-plays" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div>
                <label className="overline text-zinc-400">Audio file (mp3 / wav)</label>
                <input ref={houseFileRef} type="file" accept="audio/*" data-testid="ha-file" className="mt-1.5 w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E6B34A] file:text-[#0A1030] file:font-semibold" />
              </div>
              <div>
                <label className="overline text-zinc-400">Sponsor logo (optional)</label>
                <input ref={houseLogoRef} type="file" accept="image/*" data-testid="ha-logo" className="mt-1.5 w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/10 file:text-white file:font-semibold" />
              </div>
              <div>
                <label className="overline text-zinc-400">Sponsor website link (optional)</label>
                <Input value={houseAd.link || ""} onChange={(e) => setHouseAd({ ...houseAd, link: e.target.value })} placeholder="https://sponsor.com" data-testid="ha-link" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <button onClick={uploadHouseAd} data-testid="save-house-ad" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors flex items-center justify-center gap-2"><Upload className="w-4 h-4" /> Upload & Submit</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Edit ad spot dialog */}
      <Dialog open={!!editSpot} onOpenChange={(o) => !o && setEditSpot(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="edit-spot-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Edit Ad Spot</DialogTitle></DialogHeader>
          {editSpot && (
            <div className="space-y-3 mt-2">
              <div><label className="overline text-zinc-400">Business / sponsor name</label><Input value={editSpot.business_name} onChange={(e) => setEditSpot({ ...editSpot, business_name: e.target.value })} data-testid="es-name" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Contact email</label><Input type="email" value={editSpot.contact_email} onChange={(e) => setEditSpot({ ...editSpot, contact_email: e.target.value })} placeholder="sponsor@email.com" data-testid="es-email" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Account holder</label><Input value={editSpot.user_name} onChange={(e) => setEditSpot({ ...editSpot, user_name: e.target.value })} data-testid="es-username" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Package name</label><Input value={editSpot.package_name} onChange={(e) => setEditSpot({ ...editSpot, package_name: e.target.value })} data-testid="es-package" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Price ($)</label><Input type="number" step="0.01" value={editSpot.price} onChange={(e) => setEditSpot({ ...editSpot, price: e.target.value })} data-testid="es-price" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="overline text-zinc-400">Duration (s)</label><Input type="number" value={editSpot.duration} onChange={(e) => setEditSpot({ ...editSpot, duration: e.target.value })} data-testid="es-duration" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Plays bought</label><Input type="number" value={editSpot.plays_purchased} onChange={(e) => setEditSpot({ ...editSpot, plays_purchased: e.target.value })} data-testid="es-plays" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Plays aired</label><Input type="number" value={editSpot.plays_aired} onChange={(e) => setEditSpot({ ...editSpot, plays_aired: e.target.value })} data-testid="es-aired" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <p className="text-xs text-zinc-500 -mt-1">Remaining after airing: <span className="text-[#E6B34A] font-semibold">{Math.max(0, Number(editSpot.plays_purchased) - Number(editSpot.plays_aired))}</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-zinc-400">Status</label>
                  <select value={editSpot.status} onChange={(e) => setEditSpot({ ...editSpot, status: e.target.value })} data-testid="es-status" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                    <option value="awaiting_payment">Awaiting payment</option>
                    <option value="pending_review">Pending review</option>
                    <option value="approved">Approved</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div><label className="overline text-zinc-400">Scheduled show</label><Input value={editSpot.program_title || "—"} disabled data-testid="es-program" className="mt-1.5 bg-[#0F1738] border-white/5 text-zinc-500" /></div>
              </div>
              {editSpot.status === "rejected" && (
                <div><label className="overline text-zinc-400">Rejection reason</label><Input value={editSpot.rejection_reason} onChange={(e) => setEditSpot({ ...editSpot, rejection_reason: e.target.value })} placeholder="Why was this rejected?" data-testid="es-rejection" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              )}
              <div><label className="overline text-zinc-400">Sponsor website link (optional)</label><Input value={editSpot.link_url} onChange={(e) => setEditSpot({ ...editSpot, link_url: e.target.value })} placeholder="https://sponsor.com" data-testid="es-link" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Start date</label><Input type="date" value={editSpot.start_date} onChange={(e) => setEditSpot({ ...editSpot, start_date: e.target.value })} data-testid="es-start-date" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] [color-scheme:dark]" /></div>
                <div><label className="overline text-zinc-400">End date</label><Input type="date" value={editSpot.end_date} onChange={(e) => setEditSpot({ ...editSpot, end_date: e.target.value })} data-testid="es-end-date" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] [color-scheme:dark]" /></div>
              </div>
              <div>
                <label className="overline text-zinc-400">Frequency</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input type="number" min="0" value={editSpot.frequency_count} onChange={(e) => setEditSpot({ ...editSpot, frequency_count: e.target.value })} data-testid="es-freq-count" className="w-24 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
                  <span className="text-sm text-zinc-400">times per</span>
                  <select value={editSpot.frequency_period} onChange={(e) => setEditSpot({ ...editSpot, frequency_period: e.target.value })} data-testid="es-freq-period" className="flex-1 rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline text-zinc-400">Between hours (airing window)</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input type="time" value={editSpot.air_start_time} onChange={(e) => setEditSpot({ ...editSpot, air_start_time: e.target.value })} data-testid="es-air-start" className="flex-1 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] [color-scheme:dark]" />
                  <span className="text-sm text-zinc-400">to</span>
                  <Input type="time" value={editSpot.air_end_time} onChange={(e) => setEditSpot({ ...editSpot, air_end_time: e.target.value })} data-testid="es-air-end" className="flex-1 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] [color-scheme:dark]" />
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">Only air this spot within this daily time window. Leave blank for any time.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Ad script</label>
                <Textarea value={editSpot.script} onChange={(e) => setEditSpot({ ...editSpot, script: e.target.value })} rows={5} placeholder="The spoken script for this ad…" data-testid="es-script" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
                <p className="text-[11px] text-zinc-500 mt-1">Editing the script updates the record. It won't re-record the existing audio.</p>
              </div>
              <div>
                <label className="overline text-zinc-400">Sponsor logo</label>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    {editSpot.has_logo && editSpot.logo_url
                      ? <img src={`${API}${editSpot.logo_url}?auth=${localStorage.getItem("token")}${editSpot._logoV ? `&v=${editSpot._logoV}` : ""}`} alt="Logo" data-testid="es-logo-img" className="w-full h-full object-cover" />
                      : <ImageIcon className="w-5 h-5 text-zinc-600" />}
                  </div>
                  <input ref={editLogoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif" onChange={uploadSpotLogo} className="hidden" data-testid="es-logo-input" />
                  <button type="button" onClick={() => editLogoRef.current?.click()} disabled={savingSpot} data-testid="es-logo-upload" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 text-sm font-semibold transition-colors disabled:opacity-60">
                    <Upload className="w-4 h-4" /> {editSpot.has_logo ? "Replace logo" : "Upload logo"}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500 pt-1">
                {editSpot.audio_url && <span className="px-2 py-1 rounded-md bg-white/5">🎧 Audio uploaded</span>}
                {editSpot.generated_by_ai && <span className="px-2 py-1 rounded-md bg-white/5">✨ AI-generated</span>}
                {editSpot.created_at && <span className="px-2 py-1 rounded-md bg-white/5">Created {fmtDate(editSpot.created_at)}</span>}
              </div>
              <button onClick={saveSpot} disabled={savingSpot} data-testid="save-edit-spot" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">{savingSpot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save changes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* User dialog */}
      <Dialog open={!!userForm} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{userForm?.id ? "Edit" : "New"} User</DialogTitle></DialogHeader>
          {userForm && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">Name</label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="uf-name" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Title</label><Input value={userForm.title} onChange={(e) => setUserForm({ ...userForm, title: e.target.value })} data-testid="uf-title" placeholder="Pastor, Sister…" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <div><label className="overline text-zinc-400">Email</label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="uf-email" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div><label className="overline text-zinc-400">Phone</label><Input type="tel" inputMode="tel" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: formatUSPhone(e.target.value) })} data-testid="uf-phone" placeholder="(555) 123-4567" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div>
                <label className="overline text-zinc-400">{userForm.id ? "New password (leave blank to keep)" : "Password"}</label>
                <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="uf-password" placeholder={userForm.id ? "••••••" : "At least 6 characters"} className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <div>
                <label className="overline text-zinc-400">Role</label>
                <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} data-testid="uf-role" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="user">User</option>
                  <option value="host">Host</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0"><label className="overline text-zinc-400">Birth date</label><Input type="date" value={userForm.birth_date} onChange={(e) => setUserForm({ ...userForm, birth_date: e.target.value })} data-testid="uf-birthdate" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] w-full min-w-0 appearance-none [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:ml-auto" /></div>
                <div className="min-w-0"><label className="overline text-zinc-400">City</label><Input value={userForm.city} onChange={(e) => setUserForm({ ...userForm, city: e.target.value })} data-testid="uf-city" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] w-full min-w-0" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="overline text-zinc-400">State / Region</label><Input value={userForm.state} onChange={(e) => setUserForm({ ...userForm, state: e.target.value })} data-testid="uf-state" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
                <div><label className="overline text-zinc-400">Country</label><Input value={userForm.country} onChange={(e) => setUserForm({ ...userForm, country: e.target.value })} data-testid="uf-country" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              </div>
              <button onClick={saveUser} disabled={savingUser} data-testid="save-user" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : (userForm.id ? "Save changes" : "Create user")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editEvent && <EventEditModal event={editEvent} onClose={() => setEditEvent(null)} onSaved={load} />}
    </div>
  );
}
